'use client'

import { formatCurrency } from '@repo/utils/numbers'
import { formatDate } from '@repo/utils/time'
import { InvoiceIcon } from '@repo/ui/InvoiceIcon'
import { Link } from 'react-router-dom'
import { SxProps } from '@mui/material'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useEffect } from 'react'
import { useInvoiceControlStore } from '@repo/store/useInvoiceControlStore'
import MUIPaper from '@mui/material/Paper'
import MUIStack from '@mui/material/Stack'
import MUITypography from '@mui/material/Typography'
import PropTypes from 'prop-types'
import styles from './InvoicesCard.module.scss'

export const InvoicesCard = ({ matterWorkspaceVisible, sx, testData }: InvoiceCardProps) => {
  const { currentMatter } = useMattersStore()
  const { fetchInvoiceControlBalances, invoiceControlBalances } = useInvoiceControlStore()
  const { clientMatterNo } = currentMatter || {}

  useEffect(() => {
    matterWorkspaceVisible &&
      clientMatterNo &&
      fetchInvoiceControlBalances({ clientMatterNo: clientMatterNo, matterWorkspaceVisible: matterWorkspaceVisible })

    !matterWorkspaceVisible && clientMatterNo && fetchInvoiceControlBalances({ clientMatterNo: clientMatterNo })
  }, [clientMatterNo, matterWorkspaceVisible])

  return (
    <MUIPaper
      data-testid="InvoicesCard"
      sx={{
        width: '100%',
        height: 'auto',
        padding: '0',
        background: 'white',
        borderRadius: 1,
        boxShadow: 'none',
        border: '1px solid #D8DCE5',
        position: 'relative',
        minHeight: '100%',
        gap: '8px',
        ...sx
      }}
    >
      <MUITypography
        sx={{
          padding: '1rem 1rem 1.45rem',
          height: '1rem'
        }}
        variant="h6"
      >
        Invoices
      </MUITypography>

      <MUIStack direction="column" gap={2} sx={{ marginTop: '1rem', overflowY: 'auto', maxHeight: '12.5rem' }}>
        {invoiceControlBalances && invoiceControlBalances.length > 0 ? (
          invoiceControlBalances.map((item: any, index: number) => (
            <MUIStack
              key={`${item.name}-${index}`}
              direction="column"
              alignItems="space-between"
              justifyContent="space-between"
              gap={0}
              sx={{
                backgroundColor: '#ECEEF2',
                maxWidth: 'calc(100% - 2rem)',
                margin: '0 1rem',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                maxHeight: '3rem'
              }}
            >
              <MUIStack direction="row" alignItems="center" justifyContent="space-between">
                <MUITypography variant="body1" margin="0" sx={{ fontWeight: 600 }}>
                  {item.clientName}
                </MUITypography>
                <MUITypography variant="body1" margin="0" sx={{ fontWeight: 600 }}>
                  {formatCurrency(item.amountDue)}
                </MUITypography>
              </MUIStack>
              <MUIStack direction="row" alignItems="center" justifyContent="space-between">
                <MUITypography variant="body1" margin="0" sx={{ color: 'text.secondary' }}>
                  REF: {item.reference}
                </MUITypography>
                <MUITypography variant="body1" margin="0" sx={{ color: 'text.secondary' }}>
                  {formatDate(item.date)}
                </MUITypography>
              </MUIStack>
            </MUIStack>
          ))
        ) : !invoiceControlBalances && testData && testData.length > 0 ? (
          testData.map((item: any, index: number) => (
            <MUIStack
              key={`${item.name}-${index}`}
              direction="column"
              alignItems="space-between"
              justifyContent="space-between"
              gap={0}
              sx={{
                backgroundColor: '#ECEEF2',
                maxWidth: 'calc(100% - 2rem)',
                margin: '0 1rem',
                borderRadius: '4px',
                padding: '0.25rem 0.5rem',
                maxHeight: '3rem'
              }}
            >
              <MUIStack direction="row" alignItems="center" justifyContent="space-between">
                <MUITypography variant="body1" margin="0" sx={{ fontWeight: 600 }}>
                  {item.clientName}
                </MUITypography>
                <MUITypography variant="body1" margin="0" sx={{ fontWeight: 600 }}>
                  {formatCurrency(item.amountDue)}
                </MUITypography>
              </MUIStack>
              <MUIStack direction="row" alignItems="center" justifyContent="space-between">
                <MUITypography variant="body1" margin="0" sx={{ color: 'text.secondary' }}>
                  REF: {item.reference}
                </MUITypography>
                <MUITypography variant="body1" margin="0" sx={{ color: 'text.secondary' }}>
                  {formatDate(item.date)}
                </MUITypography>
              </MUIStack>
            </MUIStack>
          ))
        ) : (
          <div className={styles.card} key="no-data-default" data-testid="emptySlide">
            <InvoiceIcon sx={{ width: '60px', height: '60px' }} />
            <MUITypography
              variant="h6"
              data-testid="no-data-message"
              sx={{
                padding: '0 0 0 4px',
                margin: '0 auto',
                textWrap: 'wrap',
                width: '100%',
                wordBreak: 'break-word',
                textAlign: 'center'
              }}
            >
              No invoices
            </MUITypography>
          </div>
        )}
      </MUIStack>
    </MUIPaper>
  )
}

export type InvoiceCardProps = {
  matterWorkspaceVisible?: boolean
  sx?: SxProps
  testData?: {
    name: string
    amount: number
    ref: string
    date: string
  }[]
}

InvoicesCard.propTypes = {
  matterWorkspaceVisible: PropTypes.bool,
  sx: PropTypes.object,
  testData: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      amount: PropTypes.number,
      ref: PropTypes.string,
      date: PropTypes.string
    })
  )
}
